import type { StructuralNode, StructuralTag } from '../api/map';
import type {
  CrossLink,
  DocId,
  Edges,
  NodeId,
  NodeMap,
  TagEdges,
  TagId,
  TagMap,
} from '../lib/types';

/**
 * Structural writes as DELTAS: the snapshot, the diff, and the replay.
 *
 * Until now `PUT /api/map` carried the WHOLE map on every save — every node's position and
 * hierarchy, every tag, and a wipe-and-recreate of the link / tag-assignment / custom-doc
 * join tables. That is why two people in the app at once fight: any save invalidates
 * everyone else's `dataVersion`, and a stale full snapshot would silently delete work the
 * sender never knew about. This module is the client half of the fix — it turns "here is the
 * map" into "here is what I changed".
 *
 * ── THE ONE RULE ─────────────────────────────────────────────────────────────────────────
 * **`x/y/side/depth` are emitted only for ids the caller MARKED as position-dirty. Every
 * other field is emitted on value difference alone.**
 *
 * The asymmetry is the whole design. Content (colour, parent, doc slots, tag fields, tag
 * assignments, custom docs, cross-links) is decided by comparing values against a baseline,
 * deliberately NOT by hand-placed dirty marks: ~30 scattered mutation sites rely on the full
 * snapshot as a safety net today, and making each of them remember to mark would turn every
 * future edit path into a chance of silent, permanent data loss. A diff cannot forget a call
 * site, and it gives deletions and no-op detection away for free.
 *
 * Positions are the exception because a value-diff CANNOT tell a drag from a collapse.
 * Canonical layout is a function of the collapse state, so expanding a branch legitimately
 * moves every sibling's coordinates — in that one person's view. Publishing those would move
 * everybody's map, which is the behaviour this release removes. So positions travel by
 * INTENT: drag, Realign, create, add-child, reparent, undo-drag. A missing mark is the only
 * silent-loss path in the design, which is why `structuralDelta.test.ts` asserts both
 * directions of it explicitly.
 *
 * ── WHAT IS AND IS NOT EXPRESSIBLE ───────────────────────────────────────────────────────
 * - A cross-link removal is EXPLICIT (`linksRemoved`), never "absent from the array": `Link`
 *   has a cuid id and no unique key on the pair, so the server replaces links per pair.
 * - A tag unassignment or a detached custom doc is absence WITHIN a declared scope
 *   (`nodeTagScope` / `customDocScope`), so the blast radius of an absence is exactly one
 *   node instead of the whole table.
 * - **Node and tag DELETION are not expressed here at all.** They go through
 *   `DELETE /api/nodes|tags/:id`, whose cascades take the join rows with them. An id that is
 *   in the baseline but gone from the current snapshot is therefore skipped everywhere — it
 *   is not a node patch, not a scope entry, and not even a `linksRemoved` pair. Such stale
 *   baseline entries are inert (nothing can ever emit them again) and disappear at the next
 *   rebase from the server.
 * - `nodeTagBlocks` have no client-side state at all — nothing in `S` holds them, so nothing
 *   here can diff them. The two wire fields exist in `StructuralDelta` because the server
 *   contract has them; this module never sets them.
 *
 * ── PURITY, AND WHY `snapshotStructure` TAKES ITS STATE ──────────────────────────────────
 * Every function here is a pure function of its arguments. `snapshotStructure` reads the live
 * map, but it takes it as a `StructureSource` parameter rather than importing `S`, so this
 * whole file imports nothing but types — which is what lets the spec drive it with fixtures
 * in the `node` test environment and what keeps `app/state.ts` out of the dependency graph of
 * a module that has no business knowing the app exists. The caller writes
 * `snapshotStructure(S)`; `S` satisfies `StructureSource` structurally.
 */

/* ------------------------------------------------------------------ the wire contract */

/** The pure output of `diffStructure`: what changed, and nothing else. */
export interface StructuralDeltaChanges {
  /** Per-node patches. Only the fields that changed; `x/y/side/depth` only when marked. */
  nodes?: Record<NodeId, Partial<StructuralNode>>;
  tags?: Record<TagId, Partial<StructuralTag>>;
  /** Added or recoloured cross-links, replaced per (unordered) pair. */
  links?: { pair: [NodeId, NodeId]; color: string | null }[];
  /** Removed cross-links. Explicit, never implied by absence. */
  linksRemoved?: [NodeId, NodeId][];
  /** The nodes whose tag assignments this delta declares. Absence inside the scope = removal. */
  nodeTagScope?: NodeId[];
  nodeTags?: [NodeId, TagId][];
  /** The nodes whose custom-doc lists this delta declares. */
  customDocScope?: NodeId[];
  customDocs?: [NodeId, DocId][];
  /** Part of the server contract; this module never emits them (see the header). */
  nodeTagBlockScope?: NodeId[];
  nodeTagBlocks?: [NodeId, TagId][];
}

/** The body `PUT /api/map` now takes. `mode` is what tells the server this is not the old
 *  whole-map payload — a body without it is refused as a stale client. */
export interface StructuralDelta extends StructuralDeltaChanges {
  /** Optimistic concurrency, unchanged: a stale value still comes back 409. */
  dataVersion: number;
  mode: 'delta';
}

/** Stamp a diff into a wire body. One place says `mode: 'delta'`, so no caller can forget it. */
export function toDeltaBody(changes: StructuralDeltaChanges, dataVersion: number): StructuralDelta {
  return { dataVersion, mode: 'delta', ...changes };
}

/* ---------------------------------------------------------------------- the snapshot */

/**
 * The shadow copy the next diff is taken against.
 *
 * Shapes chosen so equality is cheap and total: tag/doc lists are SORTED (so "did this list
 * change" is a string comparison) and links are keyed by their unordered pair (so a link is
 * one entry however it was drawn).
 */
export interface StructuralSnapshot {
  nodes: Record<NodeId, StructuralNode>;
  tags: Record<TagId, StructuralTag>;
  /** Sorted. Nodes with no tags are absent rather than holding an empty array. */
  nodeTags: Record<NodeId, TagId[]>;
  /** Sorted. Nodes with no custom docs are absent. */
  customDocs: Record<NodeId, DocId[]>;
  /** `linkKey(a, b)` -> colour. */
  links: Record<string, string | null>;
}

/** What `snapshotStructure` needs from the app state. `S` satisfies it structurally. */
export interface StructureSource {
  readonly nodes: NodeMap;
  /** INVERTED, as everywhere else: `edges[childId] = parentId`. */
  readonly edges: Edges;
  readonly tags: TagMap;
  readonly tagEdges: TagEdges;
  readonly links: readonly CrossLink[];
}

/**
 * A cross-link's identity: the two node ids, ORDER-INDEPENDENT.
 *
 * NUL is the separator because it cannot occur in a cuid — any printable separator would
 * be a collision waiting for the day ids stop being cuids. Orientation is deliberately not
 * part of the identity: a dotted link between two nodes is one link, and the server replaces
 * it per pair.
 */
export const LINK_KEY_SEP = '\u0000';

export function linkKey(a: NodeId, b: NodeId): string {
  return a < b ? a + LINK_KEY_SEP + b : b + LINK_KEY_SEP + a;
}

export function parseLinkKey(key: string): [NodeId, NodeId] {
  const i = key.indexOf(LINK_KEY_SEP);
  return [key.slice(0, i), key.slice(i + 1)];
}

/**
 * Read the live map into a snapshot — the same field-for-field mapping the whole-map payload
 * used, so a delta describes exactly the columns the bulk resync used to overwrite.
 *
 * `collapsed` is absent from nodes on purpose and stays that way: branch expansion is
 * personal, per-user view state and is never synced to anyone else. Tag collapse IS shared —
 * it is tag structure — and is included.
 */
export function snapshotStructure(src: StructureSource): StructuralSnapshot {
  const nodes: Record<NodeId, StructuralNode> = {};
  const nodeTags: Record<NodeId, TagId[]> = {};
  const customDocs: Record<NodeId, DocId[]> = {};
  for (const id in src.nodes) {
    const n = src.nodes[id];
    nodes[id] = {
      x: n.x ?? null,
      y: n.y ?? null,
      side: n.side ?? null,
      depth: n.depth ?? null,
      color: n.color || null,
      root: !!n.root,
      parentId: src.edges[id] !== undefined ? src.edges[id] : null,
      docId: n.docId || null,
      internalDocId: n.internalDocId || null,
      courseDocId: n.courseDocId || null,
      demoDocId: n.demoDocId || null,
    };
    const tg = n.tags;
    if (tg && tg.length) nodeTags[id] = [...tg].sort();
    const cd = n.customDocs;
    if (cd && cd.length) customDocs[id] = [...cd].sort();
  }

  const tags: Record<TagId, StructuralTag> = {};
  for (const id in src.tags) {
    const t = src.tags[id];
    tags[id] = {
      color: t.color || null,
      collapsed: !!t.collapsed,
      sortOrder: t.order || 0,
      parentTagId: src.tagEdges[id] !== undefined ? src.tagEdges[id] : null,
      categoryId: t.categoryId || null,
      invisible: !!t.invisible,
    };
  }

  // Keyed, so a pair listed twice (which the old array-shaped payload would have turned into
  // two rows) is one link. Last colour wins, as it did when the server recreated the table.
  const links: Record<string, string | null> = {};
  for (const [a, b, color] of src.links) links[linkKey(a, b)] = color || null;

  return { nodes, tags, nodeTags, customDocs, links };
}

/** An empty baseline: what to diff against before anything has been loaded. */
export function emptySnapshot(): StructuralSnapshot {
  return { nodes: {}, tags: {}, nodeTags: {}, customDocs: {}, links: {} };
}

/* --------------------------------------------------------------------------- the diff */

/**
 * Every structural node field, classified.
 *
 * A `Record<keyof StructuralNode, …>` rather than two string arrays, so adding a field to
 * `StructuralNode` without deciding whether it travels by value-diff or by intent is a
 * compile error rather than a field that silently stops being saved.
 */
const NODE_FIELD_KIND: Record<keyof StructuralNode, 'position' | 'content'> = {
  x: 'position',
  y: 'position',
  side: 'position',
  depth: 'position',
  color: 'content',
  root: 'content',
  parentId: 'content',
  docId: 'content',
  internalDocId: 'content',
  courseDocId: 'content',
  demoDocId: 'content',
};
const NODE_FIELDS = Object.keys(NODE_FIELD_KIND) as (keyof StructuralNode)[];

/** Tags have no position half — all six fields travel by value-diff. */
const TAG_FIELDS = Object.keys({
  color: 1,
  collapsed: 1,
  sortOrder: 1,
  parentTagId: 1,
  categoryId: 1,
  invisible: 1,
} satisfies Record<keyof StructuralTag, 1>) as (keyof StructuralTag)[];

function copyField<T, K extends keyof T>(from: T, to: Partial<T>, key: K): void {
  to[key] = from[key];
}

const EMPTY_LIST: readonly string[] = [];

/** Sorted lists, so "did this change" is a string comparison. */
function sameList(a: readonly string[] | undefined, b: readonly string[] | undefined): boolean {
  const x = a ?? EMPTY_LIST;
  const y = b ?? EMPTY_LIST;
  if (x.length !== y.length) return false;
  for (let i = 0; i < x.length; i++) if (x[i] !== y[i]) return false;
  return true;
}

/**
 * What changed between two snapshots.
 *
 * `positionDirty` names the ids whose coordinates the user actually MEANT to publish (see the
 * header). A marked id whose coordinates did not in fact move emits nothing — the mark is
 * permission to send, not an obligation — which is what keeps a Realign that changed nothing
 * from costing a request.
 *
 * An id in `next` that the baseline has never heard of (a node just created, or one restored
 * by an undo under a NEW id) is fully dirty: every content field is emitted, because there is
 * no value to compare against and "unchanged" would mean "never written". Its positions are
 * still gated by the mark.
 *
 * An id in the baseline but gone from `next` is skipped everywhere: deletion is not this
 * payload's business.
 */
export function diffStructure(
  base: StructuralSnapshot,
  next: StructuralSnapshot,
  positionDirty: ReadonlySet<NodeId>,
): StructuralDeltaChanges {
  const out: StructuralDeltaChanges = {};

  /* nodes */
  const nodePatches: Record<NodeId, Partial<StructuralNode>> = {};
  let anyNode = false;
  for (const id in next.nodes) {
    const nextNode = next.nodes[id];
    const baseNode = base.nodes[id];
    const patch: Partial<StructuralNode> = {};
    let any = false;
    for (const f of NODE_FIELDS) {
      if (NODE_FIELD_KIND[f] === 'position' && !positionDirty.has(id)) continue;
      if (baseNode && Object.is(baseNode[f], nextNode[f])) continue;
      copyField(nextNode, patch, f);
      any = true;
    }
    if (any) {
      nodePatches[id] = patch;
      anyNode = true;
    }
  }
  if (anyNode) out.nodes = nodePatches;

  /* tags */
  const tagPatches: Record<TagId, Partial<StructuralTag>> = {};
  let anyTag = false;
  for (const id in next.tags) {
    const nextTag = next.tags[id];
    const baseTag = base.tags[id];
    const patch: Partial<StructuralTag> = {};
    let any = false;
    for (const f of TAG_FIELDS) {
      if (baseTag && Object.is(baseTag[f], nextTag[f])) continue;
      copyField(nextTag, patch, f);
      any = true;
    }
    if (any) {
      tagPatches[id] = patch;
      anyTag = true;
    }
  }
  if (anyTag) out.tags = tagPatches;

  /* cross-links: added or recoloured, then removed */
  const links: { pair: [NodeId, NodeId]; color: string | null }[] = [];
  for (const key in next.links) {
    const color = next.links[key];
    if (key in base.links && Object.is(base.links[key], color)) continue;
    links.push({ pair: parseLinkKey(key), color });
  }
  if (links.length) out.links = links;

  const removed: [NodeId, NodeId][] = [];
  for (const key in base.links) {
    if (key in next.links) continue;
    const pair = parseLinkKey(key);
    // A link that vanished because one of its nodes was deleted is the cascade's business,
    // not ours — and naming a dead id here is exactly what "deletion is not expressed" forbids.
    if (!(pair[0] in next.nodes) || !(pair[1] in next.nodes)) continue;
    removed.push(pair);
  }
  if (removed.length) out.linksRemoved = removed;

  /* tag assignments and custom docs: scope + pairs */
  const tagScope = changedListScope(base.nodeTags, next.nodeTags, next.nodes);
  if (tagScope.length) {
    out.nodeTagScope = tagScope;
    out.nodeTags = pairsFor(tagScope, next.nodeTags);
  }
  const docScope = changedListScope(base.customDocs, next.customDocs, next.nodes);
  if (docScope.length) {
    out.customDocScope = docScope;
    out.customDocs = pairsFor(docScope, next.customDocs);
  }

  return out;
}

/** The nodes whose list changed — from either side, so a list that emptied is still scoped. */
function changedListScope(
  base: Record<NodeId, string[]>,
  next: Record<NodeId, string[]>,
  liveNodes: Record<NodeId, unknown>,
): NodeId[] {
  const scope: NodeId[] = [];
  const seen = new Set<NodeId>();
  for (const id of [...Object.keys(base), ...Object.keys(next)]) {
    if (seen.has(id)) continue;
    seen.add(id);
    if (!(id in liveNodes)) continue; // deleted: the cascade takes its join rows
    if (!sameList(base[id], next[id])) scope.push(id);
  }
  return scope;
}

function pairsFor<T extends string>(scope: NodeId[], lists: Record<NodeId, T[]>): [NodeId, T][] {
  const pairs: [NodeId, T][] = [];
  for (const id of scope) for (const v of lists[id] ?? []) pairs.push([id, v]);
  return pairs;
}

/**
 * Is there anything to send?
 *
 * The flush short-circuits on this — no request at all — which is what makes every remaining
 * accidental `save()` free, and what stops one client's no-op bumping everyone else's
 * `dataVersion`.
 */
export function isEmptyDelta(d: StructuralDeltaChanges): boolean {
  if (d.nodes && Object.keys(d.nodes).length) return false;
  if (d.tags && Object.keys(d.tags).length) return false;
  if (d.links && d.links.length) return false;
  if (d.linksRemoved && d.linksRemoved.length) return false;
  if (d.nodeTagScope && d.nodeTagScope.length) return false;
  if (d.customDocScope && d.customDocScope.length) return false;
  if (d.nodeTagBlockScope && d.nodeTagBlockScope.length) return false;
  return true;
}

/* -------------------------------------------------------------------------- the replay */

const BLANK_NODE: StructuralNode = {
  x: null,
  y: null,
  side: null,
  depth: null,
  color: null,
  root: false,
  parentId: null,
  docId: null,
  internalDocId: null,
  courseDocId: null,
  demoDocId: null,
};

const BLANK_TAG: StructuralTag = {
  color: null,
  collapsed: false,
  sortOrder: 0,
  parentTagId: null,
  categoryId: null,
  invisible: false,
};

/**
 * Advance the baseline by replaying a delta onto it — the ONLY way the baseline moves after a
 * successful write.
 *
 * It must not be advanced by swallowing live state instead: between building a payload and
 * its 200 the user has kept editing, and adopting the current state would quietly mark those
 * edits as already-sent. Replaying what was actually sent makes the baseline mean exactly
 * "what the server has because of us".
 *
 * Which is also why a field the delta did NOT carry keeps its baseline value — an unmarked
 * position stays at the coordinates the server still holds, not the ones on screen. A node
 * the baseline has never seen starts from blank defaults for anything the delta omits; only
 * positions can be omitted for such a node, and positions are gated by the mark anyway, so
 * the placeholder can never suppress a later write.
 */
export function applyDeltaToSnapshot(
  base: StructuralSnapshot,
  delta: StructuralDeltaChanges,
): StructuralSnapshot {
  const out: StructuralSnapshot = {
    nodes: { ...base.nodes },
    tags: { ...base.tags },
    nodeTags: { ...base.nodeTags },
    customDocs: { ...base.customDocs },
    links: { ...base.links },
  };

  for (const id in delta.nodes ?? {}) {
    out.nodes[id] = { ...(base.nodes[id] ?? BLANK_NODE), ...delta.nodes![id] };
  }
  for (const id in delta.tags ?? {}) {
    out.tags[id] = { ...(base.tags[id] ?? BLANK_TAG), ...delta.tags![id] };
  }

  for (const { pair, color } of delta.links ?? []) out.links[linkKey(pair[0], pair[1])] = color;
  for (const pair of delta.linksRemoved ?? []) delete out.links[linkKey(pair[0], pair[1])];

  applyScopedLists(out.nodeTags, delta.nodeTagScope, delta.nodeTags);
  applyScopedLists(out.customDocs, delta.customDocScope, delta.customDocs);

  return out;
}

/** Inside a scope the payload is authoritative: whatever is absent was removed. */
function applyScopedLists(
  target: Record<NodeId, string[]>,
  scope: NodeId[] | undefined,
  pairs: [NodeId, string][] | undefined,
): void {
  if (!scope || !scope.length) return;
  const next = new Map<NodeId, string[]>();
  for (const [id, v] of pairs ?? []) {
    const list = next.get(id);
    if (list) list.push(v);
    else next.set(id, [v]);
  }
  for (const id of scope) {
    const list = next.get(id);
    if (list && list.length) target[id] = list.slice().sort();
    else delete target[id];
  }
}
